import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  ARTICLE_CODE_HEADER,
  QTY_HEADER,
  buildShipmentExcelPreview,
  parseAndValidateShipmentExcelWithCatalog,
} from '../utils/shipmentExcelImport'

function formatDeltaLabel(delta) {
  if (delta > 0) return `+${delta}`
  if (delta < 0) return String(delta)
  return '0'
}

function deltaColor(delta) {
  if (delta > 0) return 'success.main'
  if (delta < 0) return 'warning.main'
  return 'text.secondary'
}

function sectionChip(section) {
  if (section === 'catalog') {
    return <Chip size="small" color="success" variant="outlined" label="New from catalog" />
  }
  if (section === 'new') {
    return <Chip size="small" color="success" variant="outlined" label="New on order" />
  }
  if (section === 'increase') {
    return <Chip size="small" color="info" variant="outlined" label="Increased" />
  }
  if (section === 'decrease') {
    return <Chip size="small" color="warning" variant="outlined" label="Decreased" />
  }
  return <Chip size="small" variant="outlined" label="Unchanged" />
}

function classifySection(row) {
  const existing = Number(row.existingQty) || 0
  const next = Number(row.newQty) || 0
  const delta = next - existing
  if (row.fromCatalog && existing <= 0 && next > 0) return 'catalog'
  if (!row.fromCatalog && existing <= 0 && next > 0) return 'new'
  if (delta > 0) return 'increase'
  if (delta < 0) return 'decrease'
  return 'unchanged'
}

/**
 * Flatten parse preview into editable rows the admin can tweak before confirm.
 */
function buildEditableRows(preview, updates, catalogAdds) {
  const orderMeta = new Map(
    (updates || []).map((u) => [u.orderLineItemId, u]),
  )
  const catalogMeta = new Map()
  for (const add of catalogAdds || []) {
    const key = add.pendingKey || add.admin_product_id
    catalogMeta.set(String(key), add)
  }

  const rows = []
  const pushOrder = (row) => {
    rows.push({
      id: `order-${row.orderLineItemId}`,
      kind: 'order',
      orderLineItemId: row.orderLineItemId,
      articleCode: row.articleCode,
      productName: row.productName,
      shippedQuantity: row.shippedQuantity,
      orderedQuantity: row.orderedQuantity,
      remainingToShip: row.remainingToShip,
      existingQty: row.existingQty,
      newQty: String(row.newQty),
      fromCatalog: false,
      qtyError: null,
    })
  }
  const pushCatalog = (row) => {
    const meta =
      catalogMeta.get(String(row.orderLineItemId)) ||
      catalogMeta.get(String(row.admin_product_id)) ||
      {}
    rows.push({
      id: `catalog-${row.orderLineItemId || row.admin_product_id}`,
      kind: 'catalog',
      admin_product_id: row.admin_product_id || meta.admin_product_id,
      pendingKey: meta.pendingKey,
      pendingAdd: meta.pendingAdd,
      unit_price: meta.unit_price,
      mrp: meta.mrp,
      articleCode: row.articleCode,
      productName: row.productName,
      shippedQuantity: 0,
      remainingToShip: 0,
      existingQty: row.existingQty,
      newQty: String(row.newQty),
      fromCatalog: true,
      qtyError: null,
    })
  }

  for (const row of preview?.newArticles || []) pushOrder(row)
  for (const row of preview?.newCatalogArticles || []) pushCatalog(row)
  for (const row of preview?.increased || []) {
    if (row.fromCatalog) pushCatalog(row)
    else pushOrder(row)
  }
  for (const row of preview?.decreased || []) {
    if (row.fromCatalog) pushCatalog(row)
    else pushOrder(row)
  }
  for (const row of preview?.unchanged || []) {
    if (row.fromCatalog) pushCatalog(row)
    else pushOrder(row)
  }

  // Ensure every update/catalog add is present even if preview section missed it
  for (const u of updates || []) {
    if (!rows.some((r) => r.kind === 'order' && r.orderLineItemId === u.orderLineItemId)) {
      const existing = orderMeta.get(u.orderLineItemId)
      rows.push({
        id: `order-${u.orderLineItemId}`,
        kind: 'order',
        orderLineItemId: u.orderLineItemId,
        articleCode: u.articleCode || existing?.articleCode || '',
        productName: '',
        shippedQuantity: 0,
        remainingToShip: 0,
        existingQty: 0,
        newQty: String(u.quantity),
        fromCatalog: false,
        qtyError: null,
      })
    }
  }

  return rows
}

function rowsToPayload(editableRows) {
  const matched = []
  const catalogMatches = []
  const updates = []
  const catalogAdds = []
  const rowErrors = []

  for (const row of editableRows) {
    const text = String(row.newQty ?? '').trim()
    if (text === '') {
      rowErrors.push({
        id: row.id,
        message: `${row.articleCode || 'Row'}: quantity is required`,
      })
      continue
    }
    if (!/^\d+$/.test(text)) {
      rowErrors.push({
        id: row.id,
        message: `${row.articleCode || 'Row'}: enter a whole number (0 or greater)`,
      })
      continue
    }
    const quantity = Number(text)
    const existing = Number(row.existingQty) || 0

    if (row.kind === 'order') {
      const remaining = Number(row.remainingToShip)
      const maxAllowed = Number.isFinite(remaining) ? remaining : null
      if (maxAllowed != null && quantity > maxAllowed) {
        rowErrors.push({
          id: row.id,
          message: `${row.articleCode || 'Row'}: qty ${quantity} exceeds remaining to ship (${maxAllowed}).`,
        })
        continue
      }
      matched.push({
        orderLineItemId: row.orderLineItemId,
        articleCode: row.articleCode,
        productName: row.productName,
        shippedQuantity: row.shippedQuantity,
        orderedQuantity: row.orderedQuantity,
        remainingToShip: row.remainingToShip,
        existingDraftQty: existing,
        quantity,
      })
      updates.push({
        orderLineItemId: row.orderLineItemId,
        quantity,
        articleCode: row.articleCode,
      })
    } else {
      catalogMatches.push({
        kind: row.pendingKey ? 'pending' : 'catalog_new',
        admin_product_id: row.admin_product_id,
        pendingKey: row.pendingKey,
        quantity,
        articleCode: row.articleCode,
        productName: row.productName,
        existingDraftQty: existing,
        unit_price: row.unit_price,
        mrp: row.mrp,
        pendingAdd: row.pendingAdd
          ? { ...row.pendingAdd, quantity: String(quantity) }
          : row.pendingAdd,
        fromCatalog: true,
      })
      catalogAdds.push({
        kind: row.pendingKey ? 'pending' : 'catalog_new',
        admin_product_id: row.admin_product_id,
        pendingKey: row.pendingKey,
        quantity,
        articleCode: row.articleCode,
        productName: row.productName,
        unit_price: row.unit_price,
        mrp: row.mrp,
        pendingAdd: row.pendingAdd
          ? { ...row.pendingAdd, quantity: String(quantity) }
          : undefined,
      })
    }
  }

  return {
    rowErrors,
    updates,
    catalogAdds,
    preview: buildShipmentExcelPreview(matched, catalogMatches),
  }
}

/**
 * Excel upload stays compact in the ship modal; preview/edit/confirm opens
 * in a separate dialog so admins can review and adjust quantities easily.
 */
export default function ShipmentExcelImportPanel({
  orderLineItems,
  qtyByLine,
  pendingAdds = [],
  orgId = null,
  canAddCatalogProduct = false,
  existingProductIds = null,
  disabled = false,
  submitting = false,
  resetToken = null,
  onConfirmCreate,
  locationId = '',
  locationField = null,
  showNoLocationsWarning = false,
  submitError = null,
  acknowledgementLabel = null,
  acknowledged = false,
  onAcknowledgedChange,
  needsAcknowledgement = false,
}) {
  const fileInputRef = useRef(null)
  const [parsing, setParsing] = useState(false)
  const [fileName, setFileName] = useState('')
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editableRows, setEditableRows] = useState([])
  const [localConfirmError, setLocalConfirmError] = useState(null)

  const resetAll = useCallback(() => {
    setErrors([])
    setWarnings([])
    setEditableRows([])
    setDialogOpen(false)
    setFileName('')
    setParsing(false)
    setLocalConfirmError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  useEffect(() => {
    resetAll()
  }, [resetToken, resetAll])

  const livePayload = useMemo(() => rowsToPayload(editableRows), [editableRows])
  const summary = livePayload.preview?.summary
  const hasQtyErrors = livePayload.rowErrors.length > 0

  const handleFileSelected = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      setParsing(true)
      setErrors([])
      setWarnings([])
      setEditableRows([])
      setDialogOpen(false)
      setLocalConfirmError(null)
      setFileName(file.name)

      try {
        const buffer = await file.arrayBuffer()
        const result = await parseAndValidateShipmentExcelWithCatalog(buffer, {
          orderLineItems,
          qtyByLine,
          pendingAdds,
          orgId,
          // Hard gate: no catalog resolve unless the admin has add-product access
          canAddCatalogProduct: canAddCatalogProduct === true,
          existingProductIds,
        })

        if (!result.ok) {
          setErrors(result.errors)
          setWarnings(result.warnings || [])
          return
        }

        // Extra safety: drop any catalog rows if permission is missing
        const safeCatalogAdds =
          canAddCatalogProduct === true ? result.catalogAdds || [] : []
        if (
          canAddCatalogProduct !== true &&
          (result.catalogAdds || []).length > 0
        ) {
          setErrors([
            {
              message:
                'This Excel file includes products not on the order. You do not have permission to add catalog products to a shipment.',
            },
          ])
          return
        }

        setErrors([])
        setWarnings(result.warnings || [])
        setEditableRows(
          buildEditableRows(
            result.preview,
            result.updates || [],
            safeCatalogAdds,
          ),
        )
        setDialogOpen(true)
      } catch (err) {
        setErrors([
          {
            message:
              err?.message ||
              'Could not read the Excel file. Use .xlsx or .xls with Article Code and Qty columns.',
          },
        ])
      } finally {
        setParsing(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [
      orderLineItems,
      qtyByLine,
      pendingAdds,
      orgId,
      canAddCatalogProduct,
      existingProductIds,
    ],
  )

  const handleQtyChange = useCallback((rowId, value) => {
    const cleaned =
      value === '' ? '' : String(value).replace(/[^\d]/g, '')
    setEditableRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, newQty: cleaned, qtyError: null } : row,
      ),
    )
    setLocalConfirmError(null)
  }, [])

  const handleCloseDialog = useCallback(
    (event) => {
      if (submitting) return
      event?.stopPropagation?.()
      event?.preventDefault?.()
      setDialogOpen(false)
      setLocalConfirmError(null)
    },
    [submitting],
  )

  const handleConfirm = useCallback(async () => {
    if (submitting || parsing || hasQtyErrors) return
    if (!locationId) {
      setLocalConfirmError(
        'Select a distribution center before creating the shipment.',
      )
      return
    }
    if (needsAcknowledgement && !acknowledged) {
      setLocalConfirmError(
        'Confirm that you understand before creating the shipment.',
      )
      return
    }
    const { updates, catalogAdds, preview, rowErrors } = livePayload
    if (rowErrors.length) return
    const safeCatalogAdds = canAddCatalogProduct ? catalogAdds : []
    if (!updates.length && !safeCatalogAdds.length) return
    setLocalConfirmError(null)
    // Keep this dialog open on API/validation failure so the admin can adjust.
    // Successful create closes the parent ship modal (resetToken).
    await onConfirmCreate?.({
      updates,
      catalogAdds: safeCatalogAdds,
      preview,
      warnings,
    })
  }, [
    submitting,
    parsing,
    hasQtyErrors,
    locationId,
    needsAcknowledgement,
    acknowledged,
    livePayload,
    canAddCatalogProduct,
    onConfirmCreate,
    warnings,
  ])

  const hasShipItems =
    livePayload.updates.length > 0 ||
    (canAddCatalogProduct && livePayload.catalogAdds.length > 0)

  const canConfirm =
    dialogOpen &&
    editableRows.length > 0 &&
    hasShipItems &&
    !hasQtyErrors &&
    !!locationId &&
    !(needsAcknowledgement && !acknowledged) &&
    !parsing &&
    !submitting &&
    !disabled

  const dialogError = localConfirmError || submitError

  return (
    <>
      <Box
        sx={{
          mb: 2,
          p: 2,
          border: '1px solid #eee',
          borderRadius: 2,
          bgcolor: '#fafafa',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Create shipment from Excel
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Upload <strong>{ARTICLE_CODE_HEADER}</strong> and{' '}
          <strong>{QTY_HEADER}</strong>. A preview dialog opens so you can
          review and edit quantities before creating the shipment. This
          shipment will include <strong>only</strong> articles from the file
          (other order lines are not shipped in this flow).
          {canAddCatalogProduct
            ? ' Off-order articles are resolved from the purchase catalog.'
            : ' Only articles already on this order are accepted.'}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            hidden
            onChange={handleFileSelected}
            disabled={disabled || submitting || parsing}
          />
          <Button
            variant="outlined"
            startIcon={
              parsing ? <CircularProgress size={16} /> : <CloudUploadIcon />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || submitting || parsing}
          >
            {parsing ? 'Reading Excel…' : 'Upload Excel'}
          </Button>
          {fileName ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {fileName}
            </Typography>
          ) : null}
          {editableRows.length > 0 && !dialogOpen ? (
            <Button
              size="small"
              variant="text"
              onClick={() => setDialogOpen(true)}
              disabled={submitting || parsing}
            >
              Reopen preview
            </Button>
          ) : null}
          {(fileName || errors.length > 0) && (
            <Button
              size="small"
              onClick={resetAll}
              disabled={submitting || parsing}
            >
              Clear
            </Button>
          )}
        </Stack>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Fix these issues before uploading again
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((err, idx) => (
                <li key={`${err.excelRow || 'e'}-${idx}`}>
                  <Typography variant="body2">{err.message}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={(_event, reason) => {
          // Ignore backdrop clicks so clearing/selecting the DC Autocomplete
          // does not accidentally dismiss the preview.
          if (reason === 'backdropClick') return
          handleCloseDialog()
        }}
        maxWidth="md"
        fullWidth
        // Nested inside CreateShipmentModal — avoid focus trap conflicts
        disableEnforceFocus
        disableAutoFocus
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="span">
              Excel shipment preview
            </Typography>
            {fileName ? (
              <Typography variant="body2" color="text.secondary">
                {fileName}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label="Close preview"
            onClick={(e) => handleCloseDialog(e)}
            disabled={submitting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Select the distribution center, edit ship quantities if needed, then
            confirm. Uploading the file alone does not create a shipment. Only
            the articles listed below will be included — other remaining order
            lines are left for a later shipment.
          </Typography>

          {locationField ? (
            <Box sx={{ mb: 1 }}>
              {locationField}
              {showNoLocationsWarning ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No distribution centers are available.
                </Alert>
              ) : null}
              {!locationId ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select a distribution center to enable Confirm & Create
                  Shipment.
                </Alert>
              ) : null}
            </Box>
          ) : null}

          {dialogError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {dialogError}
            </Alert>
          ) : null}

          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Quantity notices
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {warnings.map((w, idx) => (
                  <li key={`${w.articleCode || 'w'}-${idx}`}>
                    <Typography variant="body2">{w.message}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {hasQtyErrors && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {livePayload.rowErrors.map((err) => (
                  <li key={err.id}>
                    <Typography variant="body2">{err.message}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {summary && (
            <>
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Chip
                  color="success"
                  variant="outlined"
                  label={`New: ${summary.newArticleCount}`}
                />
                {canAddCatalogProduct &&
                summary.newCatalogArticleCount > 0 ? (
                  <Chip
                    color="success"
                    variant="outlined"
                    label={`Catalog: ${summary.newCatalogArticleCount}`}
                  />
                ) : null}
                <Chip
                  color="info"
                  variant="outlined"
                  label={`Increased: ${summary.increasedCount}`}
                />
                <Chip
                  color="warning"
                  variant="outlined"
                  label={`Decreased: ${summary.decreasedCount}`}
                />
                <Chip
                  variant="outlined"
                  label={`Unchanged: ${summary.unchangedCount}`}
                />
                <Chip
                  variant="outlined"
                  label={`Qty Δ +${summary.totalQtyAdded} / -${summary.totalQtyRemoved}`}
                />
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
            </>
          )}

          <TableContainer sx={{ overflowX: 'auto', maxHeight: 420 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Article</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Shipped</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right">Draft</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    Ship qty
                  </TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableRows.map((row) => {
                  const existing = Number(row.existingQty) || 0
                  const next = Number(row.newQty)
                  const delta = Number.isFinite(next) ? next - existing : 0
                  const remaining =
                    row.kind === 'order' && Number.isFinite(Number(row.remainingToShip))
                      ? Number(row.remainingToShip)
                      : null
                  const overRemaining =
                    row.kind === 'order' &&
                    remaining != null &&
                    Number.isFinite(next) &&
                    next > remaining
                  const section = classifySection({
                    ...row,
                    existingQty: existing,
                    newQty: Number.isFinite(next) ? next : 0,
                  })
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.articleCode || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.productName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{sectionChip(section)}</TableCell>
                      <TableCell align="right">{row.shippedQuantity}</TableCell>
                      <TableCell align="right">
                        {remaining != null ? remaining : '—'}
                      </TableCell>
                      <TableCell align="right">{existing}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          value={row.newQty}
                          onChange={(e) =>
                            handleQtyChange(row.id, e.target.value)
                          }
                          disabled={submitting}
                          inputProps={{
                            inputMode: 'numeric',
                            'aria-label': `Ship qty for ${row.articleCode}`,
                            style: { textAlign: 'right' },
                            max: remaining != null ? remaining : undefined,
                          }}
                          error={Boolean(
                            overRemaining ||
                              livePayload.rowErrors.some((e) => e.id === row.id),
                          )}
                          helperText={
                            overRemaining
                              ? `Max ${remaining}`
                              : undefined
                          }
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: deltaColor(
                              Number.isFinite(next) ? delta : 0,
                            ),
                          }}
                        >
                          {row.newQty === ''
                            ? '—'
                            : `${existing} → ${row.newQty} (${formatDeltaLabel(delta)})`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {acknowledgementLabel ? (
            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => onAcknowledgedChange?.(e.target.checked)}
                  disabled={submitting}
                  inputProps={{ 'aria-label': 'I understand' }}
                />
              }
              label={acknowledgementLabel}
            />
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={(e) => handleCloseDialog(e)}
            disabled={submitting}
          >
            Back
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              resetAll()
            }}
            disabled={submitting}
            color="inherit"
          >
            Discard file
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Confirm & Create Shipment'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
